import {
  AlertCircle,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  MapPin,
  MessageSquare,
  Users,
} from "lucide-react";
import {
  DailyTaskRwo,
  DwoPerformance,
  FieldActivity,
  Guidance,
} from "./types/dailyTaskRwo";
import { motion } from "framer-motion";

export const formatDate = (dateStr: string) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const SectionHeading = ({
  children,
  count,
}: {
  children: React.ReactNode;
  count?: number;
}) => (
  <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
    {children}
    {typeof count === "number" && ` (${count})`}
  </p>
);

const StatTile = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
}) => (
  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
    <div className="bg-gray-50 rounded-lg p-2">
      <Icon className="size-4 text-gray-500" />
    </div>
    <div>
      <p className="text-lg font-semibold text-gray-900 leading-none">
        {value}
      </p>
      <p className="text-[11px] text-gray-400 mt-1">{label}</p>
    </div>
  </div>
);

const DwoOversightSummary = ({ task }: { task: DailyTaskRwo }) => (
  <div>
    <SectionHeading>DWO Oversight</SectionHeading>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      <StatTile
        label="Total DWOs Assigned"
        value={task.total_dwo_assigned}
        icon={Users}
      />
      <StatTile
        label="DWO/s Contacted Today"
        value={task.dwo_contacted_today}
        icon={ClipboardCheck}
      />
      <StatTile
        label="DWO Task-Reports Submitted"
        value={task.dwo_reports_submitted}
        icon={ClipboardList}
      />
      <StatTile
        label="DWO Activities Reviewed"
        value={task.dwo_activities_reviewed}
        icon={ClipboardList}
      />
      <StatTile
        label="DWO Issues Resolved"
        value={task.dwo_issues_resolved}
        icon={AlertCircle}
      />
      <StatTile
        label="DWO/s Given Guidance"
        value={task.dows_given_guidance}
        icon={MessageSquare}
      />
    </div>
  </div>
);

const OVERVIEW_METRICS: {
  label: string;
  team: keyof DailyTaskRwo;
  self: keyof DailyTaskRwo;
}[] = [
  {
    label: "Organisations Visited",
    self: "self_organisations_visited",
    team: "team_organisations_visited",
  },
  {
    label: "Approvals Received",
    self: "self_approvals_received",
    team: "team_approvals_received",
  },
  {
    label: "Health Awareness Sessions",
    self: "self_health_awareness_sessions",
    team: "team_health_awareness_sessions",
  },
  {
    label: "Individuals Registered",
    self: "self_total_participants_registered",
    team: "team_total_participants_registered",
  },

  {
    label: "Health Checkups Conducted",
    self: "self_health_checkups_conducted",
    team: "team_health_checkups_conducted",
  },
  {
    label: "Health Reports Updated on the Portal",
    self: "self_health_reports_added",
    team: "team_health_reports_added",
  },
  {
    label: "Follow-ups Completed",
    self: "self_followups_completed",
    team: "team_followups_completed",
  },
  {
    label: "Supplement Orders Confirmed",
    self: "self_supplement_order_confirmed_today",
    team: "team_supplement_order_confirmed_today",
  },
];

const SelfandTeamOverview = ({ task }: { task: DailyTaskRwo }) => (
  <div>
    <SectionHeading>Self and Team — Today</SectionHeading>
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      <div className="grid grid-cols-5 bg-gray-50 text-[11px] font-medium text-gray-500 uppercase tracking-wide px-4 py-2">
        <span className="col-span-2">Metric</span>
        <span className="text-right">Self</span>
        <span className="text-right">Team</span>
        <span className="text-right">Total</span>
      </div>
      {OVERVIEW_METRICS.map((metric) => (
        <div
          key={metric.label}
          className="grid grid-cols-5 px-4 py-2.5 text-sm border-t border-gray-50"
        >
          <span className="text-gray-600 col-span-2">{metric.label}</span>
          <span className="text-right font-medium text-gray-900">
            {String(task[metric.self])}
          </span>
          <span className="text-right font-medium text-gray-900">
            {String(task[metric.team])}
          </span>
          <span className="text-right font-medium text-gray-900">
            {Number(task[metric.team]) + Number(task[metric.self])}
          </span>
        </div>
      ))}
    </div>
  </div>
);

type TierRow = {
  label: string;
  self_3: keyof DailyTaskRwo;
  self_12: keyof DailyTaskRwo;
  team_3: keyof DailyTaskRwo;
  team_12: keyof DailyTaskRwo;
};

const MEMBERSHIP_TIERS: TierRow[] = [
  {
    label: "Silver Membership",
    self_3: "self_silver_members_registered",
    self_12: "self_silver_members_registered_12",
    team_3: "team_silver_members_registered",
    team_12: "team_silver_members_registered_12",
  },
  {
    label: "Gold Membership",
    self_3: "self_gold_members_registered",
    self_12: "self_gold_members_registered_12",
    team_3: "team_gold_members_registered",
    team_12: "team_gold_members_registered_12",
  },
  {
    label: "Platinum Membership",
    self_3: "self_platinum_members_registered",
    self_12: "self_platinum_members_registered_12",
    team_3: "team_platinum_members_registered",
    team_12: "team_platinum_members_registered_12",
  },
  {
    label: "Star Membership",
    self_3: "self_star_members_registered",
    self_12: "self_star_members_registered_12",
    team_3: "team_star_members_registered",
    team_12: "team_star_members_registered_12",
  },
  {
    label: "Diamond Membership",
    self_3: "self_diamond_members_registered",
    self_12: "self_diamond_members_registered_12",
    team_3: "team_diamond_members_registered",
    team_12: "team_diamond_members_registered_12",
  },
];

const MembershipTiersTable = ({ task }: { task: DailyTaskRwo }) => (
  <div>
    <SectionHeading>Membership Subscriptions</SectionHeading>
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-x-auto">
      <div className="grid grid-cols-7 bg-gray-50 text-[11px] font-medium text-gray-500 uppercase tracking-wide px-4 py-2 min-w-[480px]">
        <span className="col-span-2">Tier</span>
        <span className="text-right">Self (3 Months)</span>
        <span className="text-right">Self (12 Months)</span>
        <span className="text-right">Team (3 Months)</span>
        <span className="text-right">Team (12 Months)</span>
        <span className="text-right">Total Weightage</span>
      </div>
      {MEMBERSHIP_TIERS.map((tier, i) => (
        <div
          key={tier.label}
          className="grid grid-cols-7 px-4 py-2.5 text-sm border-t border-gray-50 min-w-[480px]"
        >
          <span className="text-gray-600 col-span-2">{tier.label}</span>
          <span className="text-right font-medium text-gray-900">
            {String(task[tier.self_3])}
          </span>
          <span className="text-right text-gray-500">
            {String(task[tier.self_12])}
          </span>
          <span className="text-right font-medium text-gray-900">
            {String(task[tier.team_3])}
          </span>
          <span className="text-right text-gray-500">
            {String(task[tier.team_12])}
          </span>
          <span className="text-right text-gray-500">
            {(i + 1) *
              (Number(task[tier.self_3]) +
                Number(task[tier.self_12]) +
                Number(task[tier.team_3]) +
                Number(task[tier.team_12]))}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const DwoPerformanceCard = ({ dwo }: { dwo: DwoPerformance }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-2"
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-medium text-gray-900">{dwo.dwo_name}</p>
      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
        {dwo.state}
      </span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-gray-500">
      <span>
        Visits Approved:{" "}
        <strong className="text-gray-900">{dwo.session_visit_approved}</strong>
      </span>

      <span>
        Visits Pending:{" "}
        <strong className="text-gray-900">{dwo.session_visit_pending}</strong>
      </span>

      <span>
        Health Sessions Conducted:{" "}
        <strong className="text-gray-900">
          {dwo.no_of_sessions_conducted_today}
        </strong>
      </span>

      <span>
        Individuals Registered:{" "}
        <strong className="text-gray-900">{dwo.participants_registered}</strong>
      </span>

      <span>
        Membership Weightage:{" "}
        <strong className="text-gray-900">
          {dwo.membership_plans_weighted_total}
        </strong>
      </span>

      <span>
        Supplement Orders Confirmed:{" "}
        <strong className="text-gray-900">
          {dwo.supplement_orders_confirmed}
        </strong>
      </span>

      <span>
        Remark: <strong className="text-gray-700">{dwo.remarks}</strong>
      </span>

      <span></span>

      <span>
        Health Reports Updated:{" "}
        <strong className="text-gray-700">{dwo.health_reports_added}</strong>
      </span>
    </div>

    <div className="flex justify-center gap-4 text-xs text-gray-500 pt-1 border-t border-gray-50">
      <span>
        Silver: <strong>{dwo.silver_total}</strong>
      </span>
      <span>
        Gold: <strong>{dwo.gold_total}</strong>
      </span>
      <span>Platinum: {dwo.platinum_total}</span>
      <span>
        Start: <strong>{dwo.star_total}</strong>
      </span>
      <span>
        Diamond: <strong>{dwo.diamond_total}</strong>
      </span>
    </div>
  </motion.div>
);

const FieldActivityCard = ({ activity }: { activity: FieldActivity }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-1.5"
  >
    <div className="flex items-center gap-2">
      <MapPin className="size-4 text-gray-400" />
      <p className="text-sm font-medium text-gray-900">
        {activity.field_location}
      </p>
    </div>
    <p className="text-xs text-gray-400">{activity.address}</p>
    <p className="text-xs text-gray-600">
      <span className="text-gray-400">Purpose: </span>
      {activity.purpose}
    </p>
    <p className="text-xs text-gray-600">
      <span className="text-gray-400">Outcome: </span>
      {activity.outcome}
    </p>
  </motion.div>
);

const GuidanceCard = ({ guidance }: { guidance: Guidance }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-1.5"
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-medium text-gray-900">
        {guidance.guidance_dwo_name}
      </p>
      {guidance.followup_required && (
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 shrink-0">
          <CalendarClock className="size-3" />
          Follow-up
        </span>
      )}
    </div>
    <p className="text-xs font-medium text-gray-600">
      Guidance Topic: {guidance.guidance_topic}
    </p>
    <p className="text-xs text-gray-500">
      Issues Discussed: {guidance.issues_discussed}
    </p>
    {guidance.followup_required && (
      <p className="text-[11px] text-gray-400 flex items-center gap-1 pt-1">
        <Calendar className="size-3" />
        Next Followup: {formatDate(guidance.followup_date)}
      </p>
    )}
  </motion.div>
);

const DailyTaskDetailsRwo = ({ task }: { task: DailyTaskRwo }) => {
  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      {/* <div>
        <p className="text-sm font-semibold text-gray-900">
          {formatDate(task.date)}
        </p>
      </div> */}

      <DwoOversightSummary task={task} />
      <SelfandTeamOverview task={task} />
      <MembershipTiersTable task={task} />

      {/* DWO performance review */}
      {task.dwo_performance_review.length > 0 && (
        <div>
          <SectionHeading count={task.dwo_performance_review.length}>
            DWO Performance Review
          </SectionHeading>
          <div className="grid gap-3">
            {task.dwo_performance_review.map((dwo) => (
              <DwoPerformanceCard key={dwo.id} dwo={dwo} />
            ))}
          </div>
        </div>
      )}

      {/* Field activity */}
      {task.field_activity.length > 0 && (
        <div>
          <SectionHeading count={task.field_activity.length}>
            Field Activity
          </SectionHeading>
          <div className="grid gap-3">
            {task.field_activity.map((activity) => (
              <FieldActivityCard key={activity.id} activity={activity} />
            ))}
          </div>
        </div>
      )}

      {/* Guidance */}
      {task.guidance.length > 0 && (
        <div>
          <SectionHeading count={task.guidance.length}>
            Guidance Given
          </SectionHeading>
          <div className="grid gap-3">
            {task.guidance.map((g) => (
              <GuidanceCard key={g.id} guidance={g} />
            ))}
          </div>
        </div>
      )}

      {/* Management update */}
      {task.management_update && (
        <div className="bg-gray-50 rounded-xl p-3">
          <SectionHeading>Management Update</SectionHeading>
          <p className="text-sm text-gray-600">{task.management_update}</p>
        </div>
      )}
    </div>
  );
};

export default DailyTaskDetailsRwo;
